/**
 * Gallery URLs for chat "פרטי חבילה" only — merges server `gallery_images` with LDS shapes
 * embedded in package_profile_full_deal (fullDeal), mirroring PackagesProfile / HotelGallery sources.
 * Does not run on first search cards — only PackageDetailsChatView.
 */
import { coerceGenericImagesToUrlList } from "./hotelProfileGalleryUrls";
import { getPackageGalleryImages } from "./packageGalleryHelpers";

function normalizePackageDetailsImageUrl(u) {
  if (u == null || typeof u !== "string") return null;
  const t = u.trim();
  if (!t) return null;
  if (t.startsWith("//")) return `https:${t}`;
  if (t.startsWith("http://") || t.startsWith("https://")) {
    return t.replace(
      /https?:\/\/(az712897\.vo\.msecnd\.net|az712897\.xyz)/g,
      "https://imagecontent.net"
    );
  }
  return null;
}

function pushDedup(out, seen, raw) {
  const n = normalizePackageDetailsImageUrl(raw);
  if (!n || seen.has(n)) return;
  seen.add(n);
  out.push(n);
}

/** Heuristic: HTTP(S) string that likely points to a hotel/package image (LDS / CDNs). */
function looksLikeHotelImageHttpUrl(n) {
  if (!n || typeof n !== "string") return false;
  const lower = n.toLowerCase();
  if (!lower.startsWith("http://") && !lower.startsWith("https://")) return false;
  if (/\.(jpe?g|png|webp|gif|bmp)(\?|#|$)/i.test(lower)) return true;
  if (/imagecontent\.net|vo\.msecnd\.net|azureedge\.net|cloudinary|imgix/i.test(lower)) {
    return true;
  }
  return false;
}

/**
 * Deep walk on hotel JSON (xmltodict quirks) to pick image URL strings we might have missed.
 */
function collectImageUrlsFromHotelDeep(hotel, out, localSeen, depth, maxDepth, maxUrls) {
  if (depth > maxDepth || out.length >= maxUrls || hotel == null) return;
  if (typeof hotel === "string") {
    const n = normalizePackageDetailsImageUrl(hotel);
    if (n && looksLikeHotelImageHttpUrl(n) && !localSeen.has(n)) {
      localSeen.add(n);
      out.push(n);
    }
    return;
  }
  if (Array.isArray(hotel)) {
    for (const x of hotel) {
      collectImageUrlsFromHotelDeep(x, out, localSeen, depth + 1, maxDepth, maxUrls);
      if (out.length >= maxUrls) return;
    }
    return;
  }
  if (typeof hotel !== "object") return;
  for (const v of Object.values(hotel)) {
    collectImageUrlsFromHotelDeep(v, out, localSeen, depth + 1, maxDepth, maxUrls);
    if (out.length >= maxUrls) return;
  }
}

/**
 * @param {Record<string, unknown>|null|undefined} view — package_details_view payload
 * @returns {string[]}
 */
export function mergePackageDetailsGalleryImages(view) {
  const seen = new Set();
  const out = [];

  const fromServer = Array.isArray(view?.gallery_images) ? view.gallery_images : [];
  for (const u of fromServer) {
    pushDedup(out, seen, typeof u === "string" ? u : null);
  }

  /** Same as PackagesProfile hero: POST /getHotelInfo → gallery_images is authoritative when non-empty. */
  if (out.length > 0) {
    return out.slice(0, 20);
  }

  const fd = view?.package_profile_full_deal;
  if (!fd || typeof fd !== "object") {
    return out;
  }

  const hotel = fd.hotel && typeof fd.hotel === "object" ? fd.hotel : null;
  if (hotel) {
    const gi = hotel.GenericImages ?? hotel.genericImages;
    const rawList =
      gi && typeof gi === "object" ? gi.images ?? gi.Images ?? gi.image ?? gi.Image : null;
    for (const u of coerceGenericImagesToUrlList(rawList)) {
      pushDedup(out, seen, u);
    }
    for (let i = 1; i <= 12; i += 1) {
      const k = `galleryImage${i}`;
      if (hotel[k] != null) pushDedup(out, seen, hotel[k]);
    }
    pushDedup(out, seen, hotel.resourceUrl);
    pushDedup(out, seen, hotel.hotelImage);
    pushDedup(out, seen, hotel.image);
    pushDedup(out, seen, hotel.mainImage);
    pushDedup(out, seen, hotel.getImage);
    pushDedup(out, seen, hotel.GetImage);

    const deepExtra = [];
    collectImageUrlsFromHotelDeep(hotel, deepExtra, new Set(seen), 0, 10, 28);
    for (const u of deepExtra) {
      pushDedup(out, seen, u);
    }
  }

  for (const u of getPackageGalleryImages(fd, null)) {
    pushDedup(out, seen, u);
  }

  if (Array.isArray(fd.images)) {
    for (const it of fd.images) {
      const u =
        typeof it === "string" ? it : it && typeof it === "object" ? it.url ?? it.resourceUrl : null;
      pushDedup(out, seen, u);
    }
  }

  return out.slice(0, 20);
}
